// tableGame — an interactive N-seat table: a human (seat 0) plays against (players-1) TSUMOGIRI CPUs,
// driven by the authoritative engine reducer. The CPUs are NOT a playing AI (spec §0 forbids that) —
// `autoChoose` just takes a win if offered, else passes calls, else discards the drawn tile. This is
// the test-bed for designing 2..6-player rules: you actually PLAY a ruleset to see it behave.
//
// The engine reducer is single-hand; this controller adds the MULTI-HAND progression (dealer rotation
// / renchan / honba / pot carry / tobi end) that the application layer owns. Pure & deterministic:
// every hand's wall comes from a seed derived from the base seed + hand index.
import type { GameState, Intent, RuleSet, Seat, TileId, YakuDefMeta, PlayerCount } from '@mj/protocol';
import { baseOfAka, isFlower, withPlayers } from '@mj/protocol';
import {
  reduce,
  legalMoves,
  shanten,
  exhaustiveDraw,
  resolveCallWindow,
  dealHand,
  finishHandOutcome,
  nextHandTransition,
  nukiTileSet,
  YAKU_CATALOG,
  type GameEvent,
} from '@mj/engine';
import { buildTargets, pickClosest, pickMeldCall, pickDiscard, type YakumanTarget } from './collector';

export type WaitKind = 'turn' | 'call' | 'handOver' | 'gameOver';

export interface HandResult {
  kind: 'win' | 'ryuukyoku';
  /** Per-seat score delta applied this hand (already reflected in scores). */
  payments: number[];
  winners: Seat[];
  /** win events carry per-winner ScoreResult; ryuukyoku carries tenpai seats. */
  detail: GameEvent | null;
}

const HUMAN: Seat = 0 as Seat;

// ---------------------------------------------------------------------------
// Event journal (§6) — a cumulative, RE-NUMBERED log of everything that happened.
// The engine's GameEvent.seq resets to 0 on every reduce() and `this.events` is
// overwritten per batch, so UI consumers (fx / ARIA live) can't build a timeline
// from it. The journal assigns a GLOBAL monotonically-increasing seq instead;
// the engine `e.seq` is kept inside each entry for in-batch ordering only.
// Consumers each keep their OWN cursor (the journal stores none).
// ---------------------------------------------------------------------------

export type JournalEvent =
  | { kind: 'handStart'; kyoku: number; dealer: Seat; honba: number;
      roundWind: TileId; scores: number[]; doraIndicators: TileId[]; myHand: TileId[] }
  | { kind: 'engine'; e: GameEvent }
  | { kind: 'handEnd'; result: 'win' | 'ryuukyoku'; winners: Seat[];
      deltas: number[]; scores: number[] }
  | { kind: 'gameOver'; reason: 'tobi'; scores: number[] };

export interface JournalEntry { seq: number; kyoku: number; ev: JournalEvent }

const MAX_ENTRIES = 2048; // 先頭 trim。seq は絶対値なので外部カーソルは壊れない

export class EventJournal {
  private entries: JournalEntry[] = [];
  private nextSeq = 0; // absolute: NEVER resets (not per hand, not on trim)

  /** = nextSeq — a cheap monotonic version stamp (React 変化検知用). */
  get head(): number {
    return this.nextSeq;
  }

  append(kyoku: number, ev: JournalEvent): void {
    this.entries.push({ seq: this.nextSeq, kyoku, ev });
    this.nextSeq += 1;
    // trim from the FRONT so memory stays bounded; absolute seq keeps old cursors valid.
    if (this.entries.length > MAX_ENTRIES) this.entries.splice(0, this.entries.length - MAX_ENTRIES);
  }

  /** Append a whole reducer batch (events arrive already in e.seq ascending order — kept as-is). */
  appendEngine(kyoku: number, events: GameEvent[]): void {
    for (const e of events) this.append(kyoku, { kind: 'engine', e });
  }

  /** Entries with seq >= cursor. A cursor pointing into the trimmed-away region is
   *  silently skipped (you get whatever still remains); past-the-head returns []. */
  sliceFrom(cursor: number): readonly JournalEntry[] {
    if (this.entries.length === 0 || cursor >= this.nextSeq) return [];
    const first = this.entries[0]!.seq; // seqs are contiguous → index by offset
    return this.entries.slice(Math.max(0, cursor - first));
  }
}

export class TableGame {
  private rs: RuleSet;
  private catalog: YakuDefMeta[];
  readonly players: PlayerCount;
  readonly humanSeat: Seat = HUMAN;
  private baseSeed: number;
  kyoku = 0; // 0-indexed hand counter
  dealer: Seat = 0 as Seat;
  honba = 0;
  private potCarry = 0;
  roundWind: TileId = 'z1';
  scores: number[];
  game: GameState;
  wait: WaitKind = 'turn';
  result: HandResult | null = null;
  events: GameEvent[] = [];
  // §6: cumulative event journal. Field initializers run before the constructor body
  // (i.e. before this.deal()), so deal()'s handStart append is safe.
  readonly journal = new EventJournal();

  // QoL (NOT a rule): auto-PASS the human's MELD call windows (pon/chii/大明槓) without prompting —
  // "鳴き確認スキップ". A RON is NEVER auto-passed (a win is never skipped). Distinct from rs.flags.noCalls
  // (which FORBIDS calling as a rule).
  skipHumanCalls = false;
  // QoL: auto-declare a WIN when available — tsumo on the human's turn, ron in a call window.
  autoWin = false;
  // 自動抜き: when ON, held nuki tiles (北/西/南 by player count) are pulled automatically on your turn
  // — like 自動和了. OFF (default, like 自動和了) = pull manually via the 抜き buttons (you may skip).
  autoNuki = false;

  // CPU policy: 'tsumogiri' (zero-decision default) | 'random' (each seat a different yakuman) |
  // 'closest' (each seat aims at the yakuman its DEAL is nearest to) | 'double' (only ≥2x-yakuman
  // targets, win only on a double) | a yakuman target id (all CPUs aim for it). A MECHANICAL collector,
  // not a playing AI (spec §0).
  cpuPolicy = 'tsumogiri';
  private targets: YakumanTarget[];
  // For 'closest'/'double' modes: the per-seat target committed at deal time (avoids per-turn churn).
  private seatTargets: (YakumanTarget | null)[] = [];

  constructor(rs: RuleSet, baseSeed: number, players: PlayerCount, catalog: YakuDefMeta[] = YAKU_CATALOG) {
    this.rs = withPlayers(rs, players); // derive count-dependent rules (e.g. 三麻/二麻 nukidora) for the actual seat count
    this.catalog = catalog;
    this.players = players;
    this.baseSeed = baseSeed;
    this.targets = buildTargets(catalog);
    this.scores = new Array(players).fill(rs.points.start);
    this.game = this.deal();
    this.commitSeatTargets();
    this.advance();
  }

  /** Apply the latest rules (save→reflect): only takes effect from the NEXT hand. */
  applyRules(rs: RuleSet, catalog: YakuDefMeta[]): void {
    this.rs = withPlayers(rs, this.players);
    this.catalog = catalog;
    this.targets = buildTargets(catalog);
  }

  /** Set the CPU policy ('tsumogiri' | 'random' | 'closest' | 'double' | <yakuman target id>). */
  setCpuPolicy(p: string): void {
    this.cpuPolicy = p;
    this.commitSeatTargets(); // (re)commit per-seat targets for closest/double on the current hands
  }

  // For 'closest'/'double': commit each CPU seat's target (from its current hand) once, to avoid churn.
  // BOTH pursue the NEAREST target over the FULL pool. 'double' differs only in the WIN gate: it accepts
  // a hand only when its TOTAL yakuman count is ≥2 — which counts COMPOUND yakuman (e.g. 大三元+字一色,
  // or all-honor 四暗刻+字一色), not just the four inherently-double targets. (Earlier this restricted
  // targets to double2x and so never recognized compound doubles — the owner's report.)
  private commitSeatTargets(): void {
    this.seatTargets = new Array(this.players).fill(null);
    if (this.cpuPolicy !== 'closest' && this.cpuPolicy !== 'double') return;
    for (let seat = 0; seat < this.players; seat++) {
      if (seat === this.humanSeat) continue;
      this.seatTargets[seat] = pickClosest(this.targets, this.game?.players[seat]?.hand ?? []) ?? null;
    }
  }

  /** Required yakuman count for a collector to declare a win (the 'double' mode holds out for ≥2x). */
  private requiredYakuman(): number {
    return this.cpuPolicy === 'double' ? 2 : 1;
  }

  /** The yakuman this seat's CPU is collecting toward (null = pure tsumogiri). */
  private targetFor(seat: Seat): YakumanTarget | null {
    if (seat === this.humanSeat) return null;
    if (this.cpuPolicy === 'tsumogiri' || this.targets.length === 0) return null;
    if (this.cpuPolicy === 'closest' || this.cpuPolicy === 'double') return this.seatTargets[seat] ?? null;
    if (this.cpuPolicy === 'random') {
      const n = this.targets.length;
      return this.targets[(((this.baseSeed + seat) % n) + n) % n] ?? null; // non-negative modulo (seed may be negative)
    }
    return this.targets.find((t) => t.id === this.cpuPolicy) ?? null;
  }

  /** Display name of a CPU seat's current collection target (null for the human / tsumogiri). */
  cpuTargetName(seat: Seat): string | null {
    return this.targetFor(seat)?.ja ?? null;
  }

  /** Max yakuman multiplier of a reduce win result (0 = not a yakuman). Collectors hold out for ≥N. */
  private winYakumanCount(r: ReturnType<typeof reduce>): number {
    if ('error' in r) return 0;
    const ev = r.events.find((e) => e.event === 'win');
    if (!ev || ev.event !== 'win') return 0;
    return ev.results.reduce((m, x) => Math.max(m, x.yakuman), 0);
  }

  // §6: the ONLY way state+events land on the controller. Behavior-identical to the old
  // `this.game = r.state; this.events = r.events;` pair, plus the journal append — so every
  // intermediate batch (bot turns, auto-pulls, call windows) is preserved in the timeline.
  private commit(r: { state: GameState; events: GameEvent[] }): void {
    this.game = r.state;
    this.events = r.events; // 既存挙動を完全温存
    this.journal.appendEngine(this.kyoku, r.events); // 中間バッチも全部残る
  }

  private deal(): GameState {
    // Multi-hand deal: shared with the server (TableDO) via @mj/engine so the wall, dealer/honba/pot
    // seating, and per-hand seed never drift between local and online.
    const s = dealHand({
      rs: this.rs,
      baseSeed: this.baseSeed,
      kyoku: this.kyoku,
      dealer: this.dealer,
      honba: this.honba,
      riichiPot: this.potCarry,
      roundWind: this.roundWind,
      scores: this.scores,
    });
    this.potCarry = 0;
    // §6: journal the hand-start snapshot (copies — `s` mutates as the hand plays out).
    this.journal.append(this.kyoku, {
      kind: 'handStart',
      kyoku: this.kyoku,
      dealer: this.dealer,
      honba: this.honba,
      roundWind: this.roundWind,
      scores: [...this.scores],
      doraIndicators: [...s.doraIndicators],
      myHand: [...s.players[HUMAN]!.hand],
    });
    return s;
  }

  /** Legal intents for the human RIGHT NOW (empty unless waiting on the human). */
  humanMoves(): Intent[] {
    if (this.wait !== 'turn' && this.wait !== 'call') return [];
    return legalMoves(this.game, this.humanSeat);
  }

  /** The tile that WILL be drawn next: the wall-top tile. 花牌任意化: a drawn flower now STAYS IN HAND like
   *  any tile (the reducer no longer auto-sets-aside on draw), so the peek shows the wall-top AS-IS — the
   *  player pulls a held flower via 花抜き ({ kind:'flowerpull' }), or the controller auto-pulls it. */
  private peekDrawTile(): TileId | null {
    // 初期自摸固定: seat 0's next draw is forced from tiles.fixedDraws, so peek must show that (the
    // reducer will draw the same tile on the acting intent). humanSeat is 0 here.
    const fixed = this.rs.tiles.fixedDraws;
    if (this.humanSeat === 0 && fixed) {
      const forced = fixed[this.game.fixedDrawCursor ?? 0];
      if (forced !== undefined) return forced;
    }
    return this.game.wall.length > 0 ? this.game.wall[this.game.wall.length - 1]! : null;
  }

  /** The tile to show SEPARATELY as the human's "drawn" tile on their turn.
   *  - 'draw' phase: the wall-top peek (not yet in hand; the reducer draws it on the acting intent).
   *  - 'discard' phase: ONLY after a KAN — the 嶺上 (rinshan) tile, which IS already in the hand. We find
   *    it via the last 'draw' event for the human and surface it so the UI can place it on the right AND
   *    keep it discardable under the riichi tsumogiri-lock (otherwise riichi+kan freezes: nothing clickable).
   *    After a pon/chii there is no draw, so this is null (discard from the hand normally). */
  humanDrawn(): TileId | null {
    if (this.wait !== 'turn') return null;
    if (this.game.phase === 'draw') return this.peekDrawTile();
    const hand = this.game.players[this.humanSeat]!.hand;
    for (let i = this.events.length - 1; i >= 0; i--) {
      const e = this.events[i]!;
      if (e.event === 'draw' && e.seat === this.humanSeat && e.tile !== undefined && hand.includes(e.tile)) return e.tile;
    }
    return null;
  }

  /** Whether the human can declare tsumo now. In 'draw' phase the to-be-drawn tile isn't in hand yet, so
   *  test hand+peek; in 'discard' phase (after a kan) the rinshan tile is already in hand (嶺上開花). */
  humanCanTsumo(): boolean {
    if (this.wait !== 'turn') return false;
    const p = this.game.players[this.humanSeat]!;
    if (this.game.phase === 'draw') {
      const drawn = this.peekDrawTile();
      return drawn !== null && shanten([...p.hand, drawn], p.melds) === -1;
    }
    return shanten(p.hand, p.melds) === -1; // post-kan: rinshan tile already in hand
  }

  /** The hand the human decides from on their turn: concealed tiles PLUS the freshly-drawn tile (which
   *  is not yet in `p.hand` — the reducer draws it on the acting intent). Empty unless it's their turn. */
  private humanDecisionHand(): TileId[] {
    if (this.wait !== 'turn') return [];
    const p = this.game.players[this.humanSeat]!;
    const drawn = this.humanDrawn();
    return drawn !== null ? [...p.hand, drawn] : [...p.hand];
  }

  /** Ankan candidates (base tiles with 4 copies) INCLUDING the freshly-drawn tile. legalMoves only sees
   *  the pre-draw 13 tiles, so a 3-in-hand + draw-the-4th ankan would otherwise never be offered. The
   *  reducer auto-draws on the ankan intent, so `{ kind:'ankan', tile }` then finds all 4. */
  humanAnkanTiles(): TileId[] {
    const counts = new Map<TileId, number>();
    for (const t of this.humanDecisionHand()) { const b = baseOfAka(t); counts.set(b, (counts.get(b) ?? 0) + 1); }
    const out: TileId[] = [];
    for (const [b, c] of counts) if (c >= 4) out.push(b);
    return out;
  }

  /** Shouminkan (加槓) candidates: a tile held (incl. the drawn tile) that extends one of the human's
   *  existing pon melds into a kan. */
  humanShouminkanTiles(): TileId[] {
    const p = this.game.players[this.humanSeat]!;
    const ponBases = new Set(p.melds.filter((m) => m.kind === 'pon').map((m) => baseOfAka(m.tiles[0]!)));
    const out = new Set<TileId>();
    for (const t of this.humanDecisionHand()) { const b = baseOfAka(t); if (ponBases.has(b)) out.add(b); }
    return [...out];
  }

  /** 北抜き / 西抜き candidates: the allowed nuki honors (flags.nukiTiles ?? ['z4']) that the human HOLDS,
   *  including the freshly-drawn tile. Empty unless sanmaKitaMode==='nukiDora'. legalMoves only sees the
   *  pre-draw 13, so (mirroring the ankan helper) this checks the 14-tile decision hand so a just-drawn
   *  North/West can be pulled too; the reducer auto-draws on the kita intent. */
  humanNukiTiles(): TileId[] {
    if (this.rs.flags.sanmaKitaMode !== 'nukiDora') return [];
    const allowed: string[] = nukiTileSet(this.rs) as string[]; // derived by player count (南西北)
    const held = new Set<string>(this.humanDecisionHand().map((t) => baseOfAka(t) as string));
    return allowed.filter((t) => held.has(t)) as TileId[];
  }

  /** 花抜き (花牌任意化): the FLOWERS the human holds (from the 14-tile decision hand, like humanNukiTiles),
   *  each pullable via { kind:'flowerpull', tile }. Empty unless flowers ON. When 自動抜き is on these are
   *  auto-pulled (so none show). The reducer auto-draws on the flowerpull intent (mirrors kita). */
  humanFlowerTiles(): TileId[] {
    if (this.rs.tiles.flowers !== true) return [];
    return this.humanDecisionHand().filter(isFlower);
  }

  /** 東西南北の特殊暗槓 (役牌東西南北): the human holds all four winds AND the wind-set yaku is enabled.
   *  Uses the 14-tile decision hand so a just-drawn 4th wind qualifies (mirrors ankan). */
  humanCanWindKan(): boolean {
    if (this.rs.enabledYaku['wind-set']?.enabled !== true) return false;
    const held = new Set<string>(this.humanDecisionHand().map((t) => baseOfAka(t) as string));
    return ['z1', 'z2', 'z3', 'z4'].every((w) => held.has(w));
  }

  /** The human acts. For a call window we resolve ALL seats at once (human intent + bot responses)
   *  via the authoritative multi-call resolver, so priority/multi-ron are correct. Returns the
   *  reducer verdict so the UI can surface "no yaku" / "not tenpai for riichi" without throwing. */
  act(intent: Intent): { ok: boolean; error?: string } {
    if (this.wait === 'turn') {
      const r = reduce(this.game, this.humanSeat, intent, this.rs, this.catalog);
      if ('error' in r) return { ok: false, error: r.error };
      this.commit(r);
    } else if (this.wait === 'call') {
      const res = this.resolveWindow(intent);
      if (!res.ok) return res;
    } else {
      return { ok: false, error: 'not your turn' };
    }
    this.advance();
    return { ok: true };
  }

  /** Toggle 鳴き確認スキップ. If turned ON while a MELD-call window is open, auto-pass it — but never a
   *  RON window (a win is not skipped). */
  setSkipCalls(b: boolean): void {
    this.skipHumanCalls = b;
    if (b && this.wait === 'call') {
      const ron = legalMoves(this.game, this.humanSeat).some((m) => m.kind === 'ron');
      if (!ron) this.pass();
    }
  }

  /** Toggle 自動和了. If turned ON while a win is available now, take it immediately (tsumo / ron). */
  setAutoWin(b: boolean): void {
    this.autoWin = b;
    if (!b) return;
    if (this.wait === 'turn' && this.humanCanTsumo()) { this.act({ kind: 'tsumo' }); return; }
    if (this.wait === 'call') {
      const ron = legalMoves(this.game, this.humanSeat).find((m) => m.kind === 'ron');
      if (ron) this.act(ron);
    }
  }

  /** Toggle 自動抜き. If turned ON during your turn with a held nuki tile (北/西/南) OR a held 花牌, pull it
   *  now (花牌任意化: flowers auto-pull under 自動抜き too). act() re-enters advance(), which loops the rest. */
  setAutoNuki(b: boolean): void {
    this.autoNuki = b;
    if (b && this.wait === 'turn') {
      const nk = this.humanNukiTiles();
      if (nk.length > 0) { this.act({ kind: 'kita', tile: nk[0] }); return; }
      const fl = this.humanFlowerTiles();
      if (fl.length > 0) this.act({ kind: 'flowerpull', tile: fl[0]! });
    }
  }

  /** Skip a callable window without calling (human chooses not to call). */
  pass(): void {
    if (this.wait === 'call') {
      this.resolveWindow({ kind: 'pass' });
      this.advance();
    }
  }

  private callers(): Seat[] {
    const s = this.game;
    const out: Seat[] = [];
    if (s.phase !== 'callWindow' || !s.lastDiscard) return out;
    for (let seat = 0; seat < this.players; seat++) {
      if (seat === s.lastDiscard.seat) continue;
      if (legalMoves(s, seat as Seat).length > 0) out.push(seat as Seat);
    }
    return out;
  }

  private resolveWindow(humanIntent: Intent): { ok: boolean; error?: string } {
    const submitted = this.callers().map((seat) =>
      seat === this.humanSeat ? { seat, intent: humanIntent } : { seat, intent: this.botIntent(seat) },
    );
    const r = resolveCallWindow(this.game, submitted, this.rs, this.catalog);
    if ('error' in r) return { ok: false, error: r.error };
    this.commit(r);
    return { ok: true };
  }

  // CPU policy in a CALL WINDOW. Tsumogiri seats always PASS. A collector RONs only if the win is a
  // yakuman (it holds out rather than settling for a cheap hand), and pon/kan a CORE tile of its target
  // if the target tolerates open melds (menzen targets never open their hand).
  private botIntent(seat: Seat): Intent {
    const target = this.targetFor(seat);
    if (!target) return { kind: 'pass' };
    const moves = legalMoves(this.game, seat);
    const ron = moves.find((m) => m.kind === 'ron');
    if (ron && this.winYakumanCount(reduce(this.game, seat, ron, this.rs, this.catalog)) >= this.requiredYakuman()) return ron;
    const ld = this.game.lastDiscard;
    const lastTile = ld ? (this.game.players[ld.seat]!.discards[ld.index]?.tile ?? null) : null;
    return pickMeldCall(target, moves, lastTile) ?? { kind: 'pass' };
  }

  // A bot's TURN. Declare tsumo if a self-draw completes a yaku-bearing hand; else pick a discard:
  // tsumogiri (zero-decision) seats throw the drawn tile, a collector keeps its target tiles and
  // discards the least-useful LEGAL tile. Handles both the 'draw' turn and the post-call 'discard'
  // phase (after the bot's own pon/kan there is NO draw — discard from the actual hand).
  private stepBotTurn(seat: Seat): void {
    const s = this.game;
    const p = s.players[seat]!;
    const target = this.targetFor(seat);
    const willDraw = s.phase === 'draw';
    const drawn = willDraw ? (this.peekDrawTile() ?? undefined) : undefined; // wall-top peek (may be a 花牌)

    // 花抜き (花牌任意化): a drawn flower now STAYS in hand, so bots auto-pull held flowers (regardless of
    // 自動抜き) before deciding — else flowers clog the bot hand. The reducer auto-draws on the intent, so
    // checking the post-draw 14-tile hand catches a just-drawn flower too. One pull per pass (advance loops).
    if (this.rs.tiles.flowers === true) {
      const hand14 = willDraw && drawn !== undefined ? [...p.hand, drawn] : [...p.hand];
      const flower = hand14.find(isFlower);
      if (flower !== undefined) {
        const r = reduce(s, seat, { kind: 'flowerpull', tile: flower }, this.rs, this.catalog);
        if (!('error' in r)) { this.commit(r); return; }
      }
    }

    // tsumo only on a self-draw that completes the hand (a no-yaku tsumo is rejected → fall back).
    // A collector HOLDS OUT: it declares tsumo only when the win is a yakuman, not for a cheap hand
    // (e.g. a 大三元 collector won't settle for 小三元). Tsumogiri seats take any valid win.
    if (willDraw && drawn !== undefined && shanten([...p.hand, drawn], p.melds) === -1) {
      const r = reduce(s, seat, { kind: 'tsumo' }, this.rs, this.catalog);
      if (!('error' in r) && (!target || this.winYakumanCount(r) >= this.requiredYakuman())) { this.commit(r); return; }
    }

    // 四槓子 collector: ankan any 4-of-a-kind toward four kans (one kan per pass; advance() loops back
    // for the post-rinshan discard / the next kan). Other targets never auto-kan.
    if (target?.special === 'suukantsu' && willDraw && drawn !== undefined) {
      const counts = new Map<string, number>();
      for (const t of [...p.hand, drawn]) { const b = baseOfAka(t); counts.set(b, (counts.get(b) ?? 0) + 1); }
      const quad = [...counts.entries()].find(([, c]) => c >= 4);
      if (quad) {
        const r = reduce(s, seat, { kind: 'ankan', tile: quad[0] as TileId }, this.rs, this.catalog);
        if (!('error' in r)) { this.commit(r); return; }
      }
    }

    // legalMoves projects only the PRE-draw 13-tile hand, so the freshly-drawn tile is NOT in it — but
    // tsumogiri-ing the drawn tile is always legal in the draw phase. Add it, else the collector could
    // never discard a just-drawn filler tile and would throw away a CORE tile instead.
    const legalDiscards = legalMoves(s, seat).filter((m) => m.kind === 'discard').map((m) => m.tile);
    const allowed = new Set<TileId>(legalDiscards);
    if (willDraw && drawn !== undefined) allowed.add(drawn);
    const hand14 = willDraw && drawn !== undefined ? [...p.hand, drawn] : [...p.hand];
    const tile = target
      ? pickDiscard(target, hand14, allowed)
      : willDraw ? drawn : legalDiscards[legalDiscards.length - 1];

    // candidate discards in priority order, then any legal move — robust to kuikae/riichi edges.
    const tileOrder: TileId[] = [];
    if (tile !== undefined) tileOrder.push(tile);
    if (willDraw && drawn !== undefined) tileOrder.push(drawn);
    tileOrder.push(...legalDiscards);
    const candidates: Intent[] = tileOrder.map((t) => ({ kind: 'discard', tile: t, tsumogiri: willDraw && t === drawn }));
    const lm = legalMoves(s, seat);
    if (lm.length > 0) candidates.push(lm[lm.length - 1]!);

    let lastErr = 'no legal move';
    for (const intent of candidates) {
      const r = reduce(s, seat, intent, this.rs, this.catalog);
      if (!('error' in r)) { this.commit(r); return; }
      lastErr = r.error;
    }
    throw new Error(`bot ${seat}: ${lastErr}`);
  }

  /** Drive the table until the human must act, a hand ends, or the game is over. */
  private advance(): void {
    for (let guard = 0; guard < 10000; guard++) {
      const s = this.game;
      if (s.phase === 'resolve' || s.phase === 'end') {
        this.finishHand();
        return;
      }
      if ((s.phase === 'draw' || s.phase === 'discard') && s.wall.length === 0) {
        const r = exhaustiveDraw(structuredClone(s), this.rs);
        this.commit(r);
        this.finishHand();
        return;
      }
      if (s.phase === 'callWindow') {
        const callers = this.callers();
        const human = this.humanSeat;
        const humanCalls = callers.includes(human) ? legalMoves(s, human) : [];
        const ron = humanCalls.find((m) => m.kind === 'ron');
        const hasNonPass = humanCalls.some((m) => m.kind !== 'pass');
        // 自動和了: take an available ron automatically.
        if (ron && this.autoWin) {
          const res = this.resolveWindow(ron);
          if (!res.ok) throw new Error(`auto-ron: ${res.error}`);
          continue;
        }
        // Stop for the human only with a REAL choice. 鳴き確認スキップ suppresses MELD-call prompts, but a
        // RON ALWAYS prompts (never auto-skip a win) — and pon/chii/大明槓 are still offered when noCalls
        // is off and skip is off.
        const humanMust = hasNonPass && (!this.skipHumanCalls || !!ron);
        if (humanMust) {
          this.wait = 'call';
          return;
        }
        // resolve the window from everyone's auto choice (bots pass; the human auto-passes meld calls too)
        const r = resolveCallWindow(s, callers.map((seat) => ({ seat, intent: this.botIntent(seat) })), this.rs, this.catalog);
        if ('error' in r) throw new Error(`call window: ${r.error}`);
        this.commit(r);
        continue;
      }
      // turn phase
      if (s.turn === this.humanSeat) {
        // 自動和了: declare tsumo automatically when the self-draw completes a yaku-bearing hand.
        if (this.autoWin && this.humanCanTsumo()) {
          const r = reduce(s, this.humanSeat, { kind: 'tsumo' }, this.rs, this.catalog);
          if (!('error' in r)) { this.commit(r); continue; }
          // no-yaku tsumo → fall through to a normal manual turn.
        }
        // 自動抜き: pull a held nuki tile (北/西/南) before handing control over. Loops (one pull per
        // pass; the replacement may itself be pullable) until none remain, then offers the turn.
        if (this.autoNuki) {
          const nk = this.humanNukiTiles();
          if (nk.length > 0) {
            const r = reduce(s, this.humanSeat, { kind: 'kita', tile: nk[0]! }, this.rs, this.catalog);
            if (!('error' in r)) { this.commit(r); continue; }
          }
          // 花抜き (花牌任意化): with 自動抜き on, also auto-pull a held flower (same loop slot as nuki).
          const fl = this.humanFlowerTiles();
          if (fl.length > 0) {
            const r = reduce(s, this.humanSeat, { kind: 'flowerpull', tile: fl[0]! }, this.rs, this.catalog);
            if (!('error' in r)) { this.commit(r); continue; }
          }
        }
        this.wait = 'turn';
        return;
      }
      this.stepBotTurn(s.turn);
    }
    throw new Error('advance: loop guard tripped');
  }

  /** A hand ended: record the result, carry scores, and decide renchan/honba/pot + tobi. The money +
   *  progression decision is shared with the server (TableDO) via `finishHandOutcome` (@mj/engine) so
   *  local and online never disagree on payments/renchan/tobi. Journaling stays here (Studio-only). */
  private finishHand(): void {
    const o = finishHandOutcome(this.game, this.events, this.rs);
    for (let i = 0; i < this.players; i++) this.scores[i] = o.scores[i]!;
    this.result = { kind: o.kind, payments: o.payments, winners: o.winners, detail: o.detail };
    this.pendingRenchan = o.renchan;
    this.wait = o.gameOver ? 'gameOver' : 'handOver';

    // §6: journal the hand summary (copies — scores keep mutating next hand), then the
    // terminal gameOver marker when tobi ends the whole game.
    this.journal.append(this.kyoku, {
      kind: 'handEnd',
      result: o.kind,
      winners: [...o.winners],
      deltas: [...o.payments],
      scores: [...this.scores],
    });
    if (o.gameOver) this.journal.append(this.kyoku, { kind: 'gameOver', reason: 'tobi', scores: [...this.scores] });
  }

  private pendingRenchan: { dealerKeeps: boolean; honbaNext: number; potCarried: number } | null = null;

  /** Advance to the next hand (after the human dismisses the result). No-op once the game is over. */
  nextHand(): void {
    if (this.wait === 'gameOver') return;
    const ho = this.pendingRenchan ?? { dealerKeeps: true, honbaNext: this.honba, potCarried: 0 };
    // dealer rotation / honba / pot carry / kyoku bump — shared with the server via @mj/engine.
    const t = nextHandTransition({ dealer: this.dealer, honba: this.honba, kyoku: this.kyoku, players: this.players }, ho);
    this.dealer = t.dealer;
    this.honba = t.honba;
    this.potCarry = t.potCarry;
    this.kyoku = t.kyoku;
    this.result = null;
    this.pendingRenchan = null;
    this.game = this.deal();
    this.commitSeatTargets();
    this.advance();
  }
}

export type { TileId };
